import React, { memo, useEffect, useState } from 'react';
import JBButton from '@jbhi-fi/lanyard-ui/components/Button';
import JBTypography from '@jbhi-fi/lanyard-ui/components/Typography';
import JBAlert from '@jbhi-fi/lanyard-ui/components/Alert';
import { styles } from './ReviewsContent.css';
import { initializeBazaarvoice } from '../utils/initializeBazaarvoice';
import Spinner from '@jbhi-fi/lanyard-ui/components/Spinner';

type ReviewsContentProps = {
  productSku: number | string;
  isActive: boolean;
  shouldShowTgaDisclaimer?: boolean;
};

const ReviewsContent = memo(({ productSku, isActive, shouldShowTgaDisclaimer = false }: ReviewsContentProps) => {
  const [shouldShowReviews, setShouldShowReviews] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const disclaimerText = shouldShowTgaDisclaimer ? window.Jbhifi.pdpReviewsContent?.tgaDisclaimerText : '';

  const loadBazaarVoice = () => {
    setHasError(false);

    // Bazaarvoice has already initialised
    if (window.$BV) {
      setIsLoading(false);
      return;
    }

    const onBVLoad = () => {
      window.$BV.configure('global', {
        productId: productSku,
        events: {
          bvRender: () => {
            setIsLoading(false);
          },
        },
      });

      // Automatically set loading to false if isPdpReviewsDefaultOpen is enabled so bvRender is not called
      if (window.Jbhifi.storeLocation === 'NZ' || window.featureFlags?.isPdpReviewsDefaultOpen) {
        //Calling early because BV render is not being called in NZ, will fix it after hearing from BV
        setIsLoading(false);
      }
    };

    const onBVError = () => {
      setIsLoading(false);
      setHasError(true);
    };

    initializeBazaarvoice(onBVLoad, onBVError);
  };

  const handleRetry = () => loadBazaarVoice();

  useEffect(() => {
    if (isActive && !shouldShowReviews) {
      setShouldShowReviews(true);
      loadBazaarVoice();
    }
  }, [isActive]);

  return shouldShowReviews ? (
    <div className={styles.root}>
      {disclaimerText ? (
        <JBAlert type="info" showIcon heading="Disclaimer">
          <div className={styles.disclaimerContent}>{disclaimerText}</div>
        </JBAlert>
      ) : null}

      {isLoading ? (
        <div className={styles.loading}>
          <Spinner />
        </div>
      ) : null}
      <div data-bv-show="review_highlights" data-bv-product-id={productSku}></div>
      <div data-bv-show="reviews" data-bv-product-id={productSku}></div>
      {hasError ? (
        <JBAlert showIcon type="warning" heading="Oops! We couldn't return reviews at the moment">
          <JBTypography margin="0 0 1rem 0">
            <JBButton type="link" onClick={handleRetry}>
              Please try again
            </JBButton>
          </JBTypography>
        </JBAlert>
      ) : null}
    </div>
  ) : null;
});
ReviewsContent.displayName = 'ReviewsContent';

export default ReviewsContent;
